import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgClass, NgForOf, NgIf } from '@angular/common';
import { Subscription } from 'rxjs';
import {ActionButtonComponent} from '../buttons/action-button.component';
import {Order, Slot, SlotDay, UserSpecificConfig} from '../../models/Order';
import {OrderService} from '../../services/order.service';
import {AppStateService} from '../../services/app-state.service';
import {AuthorizedApiService} from '../../services/authorized-api.service';
import {BottomSheetService} from '../../services/bottom-sheet.service';
import {TipButtonComponent} from '../buttons/tip-button.component';
import {BaseButtonComponent} from '../buttons/base-button.component';
import {LoadingSpinnerComponent} from './loading-spinner-component';
import {CircleButtonComponent} from '../buttons/circle-button.component';
import {TranslateService} from '../../services/translate.service';

@Component({
  selector: 'app-select-date-time',
  standalone: true,
  imports: [
    FormsModule,
    NgForOf,
    NgIf,
    NgClass,
    ActionButtonComponent,
    TipButtonComponent,
    BaseButtonComponent,
    LoadingSpinnerComponent,
    CircleButtonComponent
  ],
  template: `
    <div class="flex flex-col py-6 -mx-4">
<!--      <div>-->
<!--        <input type="date" class="w-full px-4 py-2 border border-gray-200 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-orange-200">-->
<!--      </div>-->
      <!-- Location Selector -->
      <div *ngIf="config && namespaces" class="mb-8">
        <div class="text-sm text-theme-text3 mb-1 px-2">Välj upphämtningsplats</div>
        <select
          class="w-full px-4 py-2 border border-theme-primary rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-orange-200"
          [disabled]="isWorking"
          [(ngModel)]="namespace"
          (ngModelChange)="nsChanged()">
          <option *ngFor="let ns of namespaces" [ngValue]="ns.id">{{ns.name}}</option>
        </select>
      </div>

      <!-- Month Selector -->
      <div class="flex justify-between items-center mb-4">
        <app-circle-button icon="fa-arrow-left" (click)="close()" class="p-1" size="10" type="solid" fontSize="lg" (onClick)="navigateMonth(-1)"
                           [disabled]="minDate && currentMonth.getFullYear() === minDate.getFullYear() && currentMonth.getMonth() === minDate.getMonth()"
        ></app-circle-button>

        <span class="text-lg font-medium">
          {{ currentMonth.toLocaleDateString('sv-SE', {month: 'long', year: 'numeric'}) }}
        </span>

        <app-circle-button icon="fa-arrow-right" (click)="close()" class="p-1" size="10" type="solid" fontSize="lg" (onClick)="navigateMonth(1)"
                           [disabled]="maxDate && currentMonth.getFullYear() === maxDate.getFullYear() && currentMonth.getMonth() === maxDate.getMonth()"
        ></app-circle-button>
      </div>

      <!-- Calendar Grid -->
      <div class="mb-6">
        <!-- Weekday Headers -->
        <div class="grid grid-cols-7 mb-1">
          <div *ngFor="let day of weekdays"
               class="text-center text-sm text-theme-text3 font-medium">
            {{ day }}
          </div>
        </div>

        <!-- Calendar Days -->
        <div class="grid grid-cols-7 gap-1">
          <button *ngFor="let day of calendarDays"
                  type="button"
                  class="aspect-square rounded-lg flex flex-col items-center justify-center"
                  [disabled]="!day.date || !day.isSelectable"
                  [ngClass]="{
          'bg-theme-surface2 text-theme-primary': day.date && day.isSelectable,
          'text-theme-primary opacity-50': day.date && !day.isSelectable,
          'text-gray-400 bg-gray-50': day.nextMonth && !day.isSelectable,
          'border-2 border-theme-primary': day.dateKey === selectedDateKey,
          'border-transparent': day.dateKey !== selectedDateKey,
          'invisible': !day.date
        }" (click)="day.isSelectable && selectedDate(day.dateKey)">
            <ng-container *ngIf="day.date">
              <div *ngIf="isToday(day.dateKey)" class="text-base font-medium rounded-full border border-theme-text3 w-8 h-8 text-theme-text flex items-center justify-center">{{ day.date }}</div>
              <div *ngIf="!isToday(day.dateKey)" class="text-base font-medium">{{ day.date }}</div>
            </ng-container>
          </button>
        </div>
      </div>

      <!-- Time Selection -->
      <div *ngIf="slots" class="mb-8 mx-4">
        <select
          class="w-full px-4 py-2 border border-gray-200 bg-white text-black rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-orange-200"
          [disabled]="isWorking"
          [(ngModel)]="time">
          <option *ngFor="let slot of slots" [ngValue]="slot.time">{{slot.time}}</option>
        </select>
      </div>

      <!-- Error Message -->
      <div *ngIf="errorMessage" class="text-center text-theme-warning font-semibold mb-4">
        {{errorMessage}}
      </div>

      <!-- No Dates Available -->
      <div *ngIf="noDates" class="text-center text-theme-warning py-4 mb-4">
        Tyvärr finns inga mer lediga tider.
      </div>

      <!-- Action Buttons -->
      <div class="flex gap-4 justify-end mt-auto relative">
        <app-action-button
          *ngIf="!noDates"
          [text]="getString('OK')"
          (onClick)="selectedTime()"
          [disabled]="isWorking">
        </app-action-button>
      </div>
      <div class="absolute left-0 bottom-8">
        <app-loading-spinner *ngIf="isWorking"></app-loading-spinner>
      </div>
    </div>
  `
})
export class SelectDateTimeComponent implements OnInit, OnDestroy {
  selectedDateKey?: string;
  config?: UserSpecificConfig;
  errorMessage?: string;
  isWorking = false;
  time: any;
  dates?: SlotDay[];
  slots?: Slot[];
  noDates = false;
  namespace?: string;
  namespaces?: any[];
  private sub?: Subscription;
  private order?: Order;

  currentMonth: Date = new Date();
  calendarDays: any[] = [];
  weekdays = ['Mån', 'Tis', 'Ons', 'Tor', 'Fre', 'Lör', 'Sön'];
  minDate: Date = new Date();
  maxDate: Date = new Date();

  constructor(
    private orderService: OrderService,
    private appState: AppStateService,
    private api: AuthorizedApiService,
    private sheetService: BottomSheetService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.sub = this.appState.observeAppState().subscribe(appState => {
      this.order = appState.order;
      this.config = appState.order.user_cfg;
      this.namespaces = appState.cfg?.dt?.takeaway?.slotting?.namespaces;

      if (this.namespaces != null) {
        console.log(this.namespaces);
      } else {
        this.fetchSlotDates();
      }
    });
  }

  ngOnDestroy() {
    this.sub?.unsubscribe();
  }

  selectedTime() {
    if (this.time == null) { return; }
    this.isWorking = true;
    this.sub?.unsubscribe();

    this.orderService.updateCollected(undefined, this.time, undefined, undefined, this.selectedDateKey, this.namespace).subscribe(() => {
      this.isWorking = false;
      this.close();
    });
  }

  selectedDate(dateKey: string) {
    this.selectedDateKey = dateKey;
    this.isWorking = true;

    // @ts-ignore
    this.api.fetchFreeSlots(dateKey, this.namespace).subscribe(slots => {
      this.isWorking = false;
      if (slots.length === 0) {
        this.noDates = true;
        this.slots = undefined;
        return;
      }

      this.noDates = false;
      this.slots = slots;

      if (slots.length > 0) {
        const sameTimeSlot = slots.find(s => s.time === this.time);
        if (sameTimeSlot) {
          this.time = sameTimeSlot.time;
        } else {
          this.time = slots[0].time;
        }
      } else {
        this.time = undefined;
      }
    });
  }

  private fetchSlotDates() {
    this.isWorking = true;
    this.dates = undefined;
    this.slots = undefined;
    this.time = undefined;

    // @ts-ignore
    this.api.fetchSlotDates(this.namespace).subscribe(dates => {
      this.isWorking = false;
      this.noDates = dates.days.length === 0;

      if (this.noDates) {
        return;
      }

      for (const d of dates.days) {
        d.date = d.date_key.substring(8);
      }

      this.dates = dates.days;
      this.setMinMaxDates();
      this.selectedDate(dates.days[0].date_key);
      this.currentMonth = new Date(this.minDate);
      this.generateCalendar();
    });
  }

  nsChanged() {
    this.fetchSlotDates();
  }

  close() {
    this.sheetService.popSheet();
  }

  generateCalendar() {
    console.log('generateCalendar for', this.currentMonth);
    this.calendarDays = [];
    const firstDayOfMonth = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth(), 1);
    const lastDayOfMonth = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() + 1, 0);

    // Get the day of the week for the first day (0 = Sunday, so we adjust for Monday = 0)
    let firstDayWeekday = firstDayOfMonth.getDay() - 1;
    if (firstDayWeekday < 0) firstDayWeekday = 6; // Sunday becomes last day

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDayWeekday; i++) {
      this.calendarDays.push({ date: null, dateKey: null, isSelectable: false });
    }

    // Add days of the month
    for (let day = 1; day <= lastDayOfMonth.getDate(); day++) {
      const date = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth(), day);
      const dateKey = this.formatDateToKey(date);
      const availableDate = this.dates?.find(d => d.date_key === dateKey);

      this.calendarDays.push({
        date: day,
        dateKey: dateKey,
        isSelectable: !!availableDate,
        weekDay: availableDate?.week_day
      });
    }

    // Calculate how many days from next month we need to add to complete 6 rows (42 cells)
    const remainingCells = 42 - this.calendarDays.length;

    // Add empty cells for days after the last day of the month (to fill all 6 rows)
    for (let i = 1; i <= remainingCells; i++) {
      this.calendarDays.push({ date: null, dateKey: null, isSelectable: false });
    }
  }

  formatDateToKey(date: Date): string {
    // Assuming date_key format is YYYY-MM-DD based on your code
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }

  navigateMonth(direction: number) {
    const newMonth = new Date(this.currentMonth);
    newMonth.setDate(1);
    newMonth.setMonth(newMonth.getMonth() + direction);

    // Only compare year and month, not the day
    if (direction < 0 && this.minDate) {
      // Check if new month is before min date's month and year
      if (newMonth.getFullYear() < this.minDate.getFullYear() ||
        (newMonth.getFullYear() === this.minDate.getFullYear() &&
          newMonth.getMonth() < this.minDate.getMonth())) {
        return;
      }
    }

    if (direction > 0 && this.maxDate) {
      // Check if new month is after max date's month and year
      if (newMonth.getFullYear() > this.maxDate.getFullYear() ||
        (newMonth.getFullYear() === this.maxDate.getFullYear() &&
          newMonth.getMonth() > this.maxDate.getMonth())) {
        return;
      }
    }

    this.currentMonth = newMonth;
    this.generateCalendar();
  }

  setMinMaxDates() {
    if (!this.dates || this.dates.length === 0) return;

    // Find earliest and latest dates to determine valid month range
    const dateObjects = this.dates.map(d => new Date(d.date_key));
    this.minDate = new Date(Math.min(...dateObjects.map(d => d.getTime())));
    this.maxDate = new Date(Math.max(...dateObjects.map(d => d.getTime())));
  }

  isToday(dateKey: string | null): boolean {
    if (!dateKey) return false;
    const today = new Date();
    const formattedToday = this.formatDateToKey(today);
    return dateKey === formattedToday;
  }

  getString(s: string) {
    return this.translate.getString(s);
  }
}
